import axios from "axios";
import { API_BASE, BACKEND_URL } from "./backendUrl";

/*
 * DT backend routes are mounted under /api/dt.
 *
 * This normalizer prevents both cases:
 *   BACKEND_URL = https://example.com
 *   BACKEND_URL = https://example.com/api
 *
 * from producing a wrong URL.
 */
const normalizeApiBase = () => {
  const raw = (API_BASE || BACKEND_URL || "").replace(/\/+$/, "");

  if (!raw) {
    return "/api";
  }

  return raw.endsWith("/api") ? raw : `${raw}/api`;
};

const API = normalizeApiBase();

const http = axios.create({
  baseURL: API,
  headers: {
    Accept: "application/json",
  },
});

/* =========================================================
   AUTH
   ========================================================= */

http.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("pps_admin_token");

    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* =========================================================
   HELPERS
   ========================================================= */

const q = (params) => {
  const clean = {};

  for (const [k, v] of Object.entries(params || {})) {
    if (v !== undefined && v !== null && v !== "") {
      clean[k] = v;
    }
  }

  return clean;
};

/* =========================================================
   DT INSPECTION API
   ========================================================= */

export const dtApi = {
  /* -------------------------------------------------------
     Constants
     GET /api/dt/constants
     ------------------------------------------------------- */

  constants: async () =>
    (await http.get("/dt/constants")).data,

  /* -------------------------------------------------------
     Inspections
     ------------------------------------------------------- */

  list: async (opts = {}) =>
    (
      await http.get("/dt/inspections", {
        params: q(opts),
      })
    ).data,

  get: async (id) =>
    (
      await http.get(
        `/dt/inspections/${encodeURIComponent(id)}`
      )
    ).data,

  create: async (payload) =>
    (
      await http.post("/dt/inspections", payload)
    ).data,

  update: async (id, payload) =>
    (
      await http.put(
        `/dt/inspections/${encodeURIComponent(id)}`,
        payload
      )
    ).data,

  remove: async (id) =>
    (
      await http.delete(
        `/dt/inspections/${encodeURIComponent(id)}`
      )
    ).data,

  duplicate: async (id) =>
    (
      await http.post(
        `/dt/inspections/${encodeURIComponent(id)}/duplicate`
      )
    ).data,

  /* -------------------------------------------------------
     PDF
     ------------------------------------------------------- */

  pdfUrl: (id) =>
    `${API}/dt/inspections/${encodeURIComponent(id)}/pdf`,

  /* -------------------------------------------------------
     Excel Export
     ------------------------------------------------------- */

  exportUrl: (opts = {}) => {
    const s = new URLSearchParams(q(opts)).toString();

    return `${API}/dt/export.xlsx${
      s ? `?${s}` : ""
    }`;
  },

  templateUrl: () =>
    `${API}/dt/template.xlsx`,

  /* -------------------------------------------------------
     Excel Import
     ------------------------------------------------------- */

  importPreview: async (file) => {
    const fd = new FormData();

    fd.append("file", file);

    return (
      await http.post(
        "/dt/import/preview",
        fd,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          timeout: 120000,
        }
      )
    ).data;
  },

  importCommit: async (payload) =>
    (
      await http.post(
        "/dt/import/commit",
        payload
      )
    ).data,

  importHistory: async () =>
    (
      await http.get("/dt/import/history")
    ).data,

  /* -------------------------------------------------------
     Photos
     ------------------------------------------------------- */

  importPhotoZip: async (file) => {
    const fd = new FormData();

    fd.append("file", file);

    return (
      await http.post(
        "/dt/photos/import-zip",
        fd,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          timeout: 300000,
        }
      )
    ).data;
  },

  unmatchedPhotos: async () =>
    (
      await http.get("/dt/photos/unmatched")
    ).data,

  assignPhoto: async (payload) =>
    (
      await http.post(
        "/dt/photos/assign",
        payload
      )
    ).data,

  /* -------------------------------------------------------
     File Upload
     Backend route: /api/files/upload
     ------------------------------------------------------- */

  uploadFile: async (file, extra = {}, { onProgress, timeout = 180000 } = {}) => {
    const fd = new FormData();

    fd.append("file", file);

    const params = q({
      folder: "inspection",
      ref_type: "inspection",
      ...extra,
    });

    return (
      await http.post(
        "/files/upload",
        fd,
        {
          params,
          timeout,
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: onProgress
            ? (e) => e.total && onProgress(Math.round((e.loaded / e.total) * 100))
            : undefined,
        }
      )
    ).data;
  },

  /* -------------------------------------------------------
     Dashboard / Master
     ------------------------------------------------------- */

  summary: async () =>
    (
      await http.get(
        "/dt/dashboard/summary"
      )
    ).data,

  master: async () =>
    (
      await http.get("/dt/master")
    ).data,

  updateMaster: async (key, values) =>
    (
      await http.put(
        `/dt/master/${encodeURIComponent(key)}`,
        {
          values,
        }
      )
    ).data,
};

/* =========================================================
   FILE URL HELPER
   ========================================================= */

export const dtFileUrl = (path) => {
  if (!path) {
    return "";
  }

  if (/^https?:/i.test(path)) {
    return path;
  }

  if (path.startsWith("/")) {
    return `${BACKEND_URL}${path}`;
  }

  return `${API}/${path}`;
};